import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";
import { MaskReveal } from "./Reveal";
import { HERO_IMG, HERO_VIDEO_HD, HERO_VIDEO_SD, BUSINESS } from "./data";

export const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1.35]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative h-screen w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ scale, y }} className="absolute inset-0">
        <video
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_IMG}
          aria-label="DJ performing in a nightclub with turntables and crowd"
        >
          <source src={HERO_VIDEO_HD} type="video/mp4" media="(min-width: 768px)" />
          <source src={HERO_VIDEO_SD} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/70 via-ink-950/40 to-ink-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-950/80 via-transparent to-ink-950/40" />
      </motion.div>

      {/* Top meta row */}
      <motion.div
        style={{ opacity }}
        className="absolute left-0 right-0 top-28 z-10 mx-auto flex max-w-[1600px] items-center justify-between px-6 md:px-12"
      >
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Est. Long Island · NY</span>
        <span className="hidden font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400 md:block">
          Weddings / Clubs / Private
        </span>
      </motion.div>

      {/* Kinetic headline */}
      <motion.div
        style={{ y: textY }}
        className="absolute inset-0 z-10 flex flex-col justify-center px-6 md:px-12"
      >
        <div className="mx-auto w-full max-w-[1600px]">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.4em] text-zinc-300"
          >
            The Sound of Every Occasion
          </motion.p>
          <h1 className="font-oswald font-700 uppercase leading-[0.82] tracking-tighter text-white">
            <MaskReveal
              lines={["Jazzy"]}
              mode="load"
              className="block text-[22vw] md:text-[12vw]"
              delay={0.5}
            />
            <MaskReveal
              lines={["The"]}
              mode="load"
              className="block text-[22vw] md:text-[12vw] text-stroke-gold"
              delay={0.65}
            />
            <MaskReveal
              lines={["Great"]}
              mode="load"
              className="block text-[22vw] md:text-[12vw]"
              delay={0.8}
            />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-8 flex max-w-xl flex-col gap-6 md:mt-10 md:flex-row md:items-end md:justify-between"
          >
            <p className="max-w-sm text-sm leading-relaxed text-zinc-400 md:text-base">
              Premium DJ experiences engineered for the dance floor — reading every room and building the moment you'll never forget.
            </p>
            <a
              data-testid="hero-book-btn"
              href="#booking"
              onClick={(e) => { e.preventDefault(); lenis ? lenis.scrollTo("#booking", { duration: 1.4 }) : document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" }); }}
              className="group inline-flex shrink-0 items-center gap-3 border border-white/20 px-7 py-4 hover:border-gold transition-colors duration-500"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white group-hover:text-gold transition-colors duration-500">
                Reserve a Date
              </span>
              <span className="h-1.5 w-1.5 bg-gold group-hover:scale-150 transition-transform duration-500" />
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
};
